/**
 * The multi-level sort dialog (spec M1-B4 §8.6, `[ui:library/table-08-sort-pane.png]`).
 *
 * Measured 556 x 172 at (558, 229): `Sort by ⓘ` + `Save to this view` (disabled), one row per
 * rule with a drag handle, `Choose column ▾`, `Ascending ▾`, then `+ New sort`.
 */
import { findColumnType, type ColumnId } from '@elizmonday/shared';
import { useTable, useTableActions } from '../context';
import type { SortDirection, SortRule } from '../settings';
import { Pane, PANE_BOX } from './Pane';

/** The empty row monday's Sort pane always shows; its column id is never a real column. */
const DRAFT_SORT_RULE = { columnId: '' as ColumnId, direction: 'asc' as SortDirection };
import { useSaveLabel } from './useSaveLabel';

export function SortPane() {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const dirty = useTable((state) => state.dirty);
  const actions = useTableActions();
  const saveLabel = useSaveLabel();

  const columns = (payload?.columns ?? []).filter((column) => findColumnType(column.type) !== undefined);
  const rules = effective.sort;

  const setRules = (next: readonly SortRule[]) => actions.setEffective({ sort: next });

  return (
    <Pane
      testId="sort-pane"
      title="Sort by"
      {...PANE_BOX.sort}
      dirty={dirty}
      canSave={payload?.permissions.canSaveView ?? false}
      saveLabel={saveLabel}
      onSave={() => void actions.saveView()}
      onClose={() => actions.openPane(null)}
    >
      {/*
        * Measured: monday's Sort pane always shows **one** row, `Choose column ▾ Ascending ▾`,
        * even before anything is sorted (`discovery/ui-reference/filters/sandbox-sort.png`).
        * Picking a column in the draft row is what creates the first rule.
        */}
      {(rules.length === 0 ? [DRAFT_SORT_RULE] : rules).map((rule, index) => (
        <div className="ez-sort-rule" key={`${rule.columnId}-${index}`} data-testid={`sort-rule-${index}`}>
          <span className="ez-sort-rule__handle" aria-hidden="true">
            ⠿
          </span>
          <select
            aria-label="Choose column"
            data-testid={`sort-column-${String(index)}`}
            value={rule.columnId}
            onChange={(event) => {
              const columnId = event.target.value as ColumnId;
              if (columnId === DRAFT_SORT_RULE.columnId) return;
              setRules(
                rules.length === 0
                  ? [{ columnId, direction: 'asc' }]
                  : rules.map((entry, at) => (at === index ? { ...entry, columnId } : entry)),
              );
            }}
          >
            {rule.columnId === DRAFT_SORT_RULE.columnId ? (
              <option value={DRAFT_SORT_RULE.columnId}>Choose column</option>
            ) : null}
            {columns.map((column) => (
              <option key={column.id} value={column.id}>
                {column.title}
              </option>
            ))}
          </select>
          <select
            aria-label="Direction"
            data-testid={`sort-direction-${String(index)}`}
            value={rule.direction}
            onChange={(event) =>
              setRules(
                rules.map((entry, at) =>
                  at === index ? { ...entry, direction: event.target.value as SortDirection } : entry,
                ),
              )
            }
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
          <button
            type="button"
            aria-label="Remove sort"
            className="ez-sort-rule__remove"
            onClick={() => setRules(rules.filter((_, at) => at !== index))}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        className="ez-board-pane__link"
        data-testid="new-sort"
        onClick={() => {
          const used = new Set(rules.map((rule) => rule.columnId as string));
          const next = columns.find((column) => !used.has(column.id)) ?? columns[0];
          if (next) setRules([...rules, { columnId: next.id, direction: 'asc' }]);
        }}
      >
        + New sort
      </button>
    </Pane>
  );
}
